import React, {useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import StyledTextField from 'components/molecules/Fields/TextField/TextField.styles';
import {useComponentProps} from 'helpers/hooks/utils';
import InputLabel from 'components/atoms/Labels/InputLabel/InputLabel';
import FormHelperText from 'components/atoms/Helpers/FormHelperText/FormHelperText';
import FilledInput from 'components/atoms/Inputs/FilledInput/FilledInput';
import Input from 'components/atoms/Inputs/Input/Input';
import OutlinedInput from 'components/atoms/Inputs/OutlinedInput/OutlinedInput';
import utils from 'helpers/utils';
import {useFormControl} from '@mui/material';
import {Span} from 'components/atoms/Text/Typography/Typography';
import Markdown from 'components/atoms/Formatters/Markdown/Markdown';
import Box from 'components/atoms/Layout/Box/Box';
import InputContainer from 'components/atoms/Layout/InputContainer/InputContainer';
import dom from 'helpers/dom';
import MentionsInput from 'components/molecules/Mentions/MentionsInput/MentionsInput';
import Mention from 'components/molecules/Mentions/Mention/Mention';
import {withMemo} from 'helpers/wrapper';
import constants from 'helpers/constants';
import EditorInput from 'components/molecules/Editors/EditorInput/EditorInput';
import EditorReadOnly from 'components/molecules/Editors/EditorReadOnly/EditorReadOnly';

const InputComponent = React.forwardRef((props, ref) => {
  const formControl = useFormControl();

  const variant = props.variant ?? formControl.variant;
  const InputVariant = variant === 'outlined' ? OutlinedInput :
    (variant === 'filled' ? FilledInput : Input);

  return <InputVariant ref={ref} {...props} variant={variant} />;
})

const TextField = withMemo(React.forwardRef((props, ref) => {
  const {
    id,
    name,
    label,
    placeholder,
    value,
    type,
    helperText,
    trim,
    format,
    size,
    rows,
    minRows,
    maxRows,
    multiline,
    markdown,
    richText,
    mentions,
    onChange,
    autoFocus,
    ellipsis,
    showTooltip,
    hiddenPlaceholder,
    renderReadOnly,
    inputProps,
    InputProps,
    InputLabelProps,
    EditorProps,
    MarkdownProps,
    FormHelperTextProps,
    ...innerProps
  } = useComponentProps({
    size: 'small',
    trim: true,
    ellipsis: true,
    showTooltip: true,
    inputProps: {},
    ...props
  }, 'TextField', {
    children: ['input', 'label', 'helper'],
    static: ['disabled', 'multiline', 'markdown', 'richText', 'ellipsis']
  });

  const innerRef = useRef(null);

  const formatValue = useCallback((value, isClean = false, remove = false) => {
    if (utils.isDefined(value)) {
      if (format) {
        return utils.fieldValue2FormattedValue(format, value, isClean, remove);
      } else if (trim) {
        return remove ? (isClean ? value : value.toString().trim()) : value;
      } else {
        return value;
      }
    } else {
      return value;
    }
  }, [format, trim]);

  const [internalValue, setInternalValue] = useState(value.toString());

  useImperativeHandle(ref, () => innerRef.current);

  useEffect(() => {
    if (autoFocus) {
      const focus = () => {
        return dom.focusElement(innerRef.current?.querySelector?.('input, textarea'));
      }

      return utils.observeRetry(focus, 5);
    }
  }, [autoFocus]);

  const handleChange = (e) => {
    const newValue = e?.target?.value ?? '';
    const formatted = formatValue(newValue, false, false);

    setInternalValue(formatValue(newValue, false, false));

    if (newValue !== formatted) {
      const start = innerRef.current?.querySelector('.MuiInputBase-input')?.selectionStart;
      if (utils.isDefined(start)) {
        if (constants.formFieldFormatTypes.numericTypes.includes(format)) {
          const newCount = newValue ? (newValue.slice(0, start).match(/[,.]/g) ?? []).length : 0;

          setTimeout(() => {
            const el = innerRef.current?.querySelector('.MuiInputBase-input');
            if (el) {
              let pos = start;
              const currentCount = el.value ? (el.value.slice(0, start).match(/[,.]/g) ?? []).length : 0;
              pos += (currentCount - newCount);
              if (pos > 0 && el.value?.[pos - 1]?.match(/[,.]/g)) {
                pos -= 1;
              }
              el.setSelectionRange(pos, pos);
            }
          });
        } else if (format) {
          const newLength = newValue ? newValue.length : 0;
          setTimeout(() => {
            const el = innerRef.current?.querySelector('.MuiInputBase-input');
            if (el) {
              let pos = start;
              const currentLength = el.value ? el.value.length : 0;
              pos += (currentLength - newLength);
              el.setSelectionRange(pos, pos);
            }
          });
        }
      }
    }

    const clean = formatValue(newValue, false, true);
    if (clean.toString() !== value.toString()) {
      onChange?.({
        nativeEvent: e?.nativeEvent,
        target: {
          name: name,
          value: clean.toString()
        }
      });
    }
  }

  useLayoutEffect(() => {
    setInternalValue((current) => {
      if (format) {
        return current !== formatValue(value.toString(), true, false) ?
          formatValue(value.toString(), true, false) : current
      } else if (trim) { // special case keep spaces in input
        return formatValue(current, false, true) !== formatValue(value.toString(), true, true) ?
          formatValue(value.toString(), true, false) : current
      } else {
        return value.toString();
      }
    });
  }, [value, format, trim, formatValue]);

  const markDownComponents = useMemo(() => {
    const renderLink = ({node, rendered, ...props}) => {
      if (props?.href) {
        const uri = utils.decodeUrl(props.href);
        const match = uri.match(/.\[(.+):(.+)\]/i);
        if (match?.length > 0) {
          const mention = mentions.find((m) => m.trigger === uri[0]);
          if (mention) {
            const Component = mention.Component ?? Mention;
            return <Component id={match[2]}
                              label={match[1]}
                              readOnly={true}
                              data={mention.data}
                              appendSpaceOnAdd={true}
                              {...mention.MentionProps}/>
          }
        }
      }

      return rendered;
    }

    return {
      a: renderLink
    };
  }, [mentions]);

  const renderValue = () => {
    if (renderReadOnly) {
      return renderReadOnly(value, {formatted: internalValue, markdown, placeholder, mentions, showTooltip, hiddenPlaceholder})
    } else {
      if (internalValue.length > 0) {
        if (mentions) {
          if (richText) {
            return <EditorReadOnly showTooltip={showTooltip} mentions={mentions} {...EditorProps}>{internalValue}</EditorReadOnly>
          } else {
            const split = utils.splitMentions(internalValue, mentions);
            if (markdown) {
              const markdown = split.reduce((a, s) => {
                if (s.mention) {
                  a.push(`[${s.display}](${s.mention.trigger}[${s.display}:${s.id}])`);
                } else {
                  a.push(s.value);
                }
                return a;
              }, []).join('');

              return <Markdown components={markDownComponents}
                               allowMentions={true}
                               {...MarkdownProps}>{markdown}</Markdown>;
            } else {
              return split.reduce((a, s, idx) => {
                if (s.mention) {
                  const Component = s.mention.Component ?? Mention;
                  a.push(<Component key={idx}
                                    id={s.id}
                                    display={s.display}
                                    readOnly={true}
                                    data={s.mention.data}
                                    appendSpaceOnAdd={true}
                                    {...s.mention.MentionProps}/>);
                } else {
                  a.push(<Span key={idx}>{s.value}</Span>);
                }
                return a;
              }, []);
            }
            }
        } else {
          if (richText) {
            return <EditorReadOnly showTooltip={showTooltip} {...EditorProps}>{internalValue}</EditorReadOnly>
          } else if (markdown) {
            return <Markdown showTooltip={showTooltip} {...MarkdownProps}>{internalValue}</Markdown>;
          } else {
            return <Span showTooltip={showTooltip}>{internalValue}</Span>;
          }
        }
      } else {
        return (placeholder && !hiddenPlaceholder) ? <Span className="placeholder">{placeholder}</Span> :
          <Span>&nbsp;</Span>;
      }
    }
  }

  const inputPropsMemo = useMemo(() => {
    return {
      ...inputProps,
      ...((mentions || richText) ? {
        mentions,
        multiline,
        rows,
        size,
        minRows: multiline ? (minRows ?? 2) : null,
        maxRows: multiline ? (maxRows ?? 6) : null,
      } : {}),
      ...(richText ? {
        ExtensionProps: {
          placeholder
        },
        ...EditorProps
      } : {}),
      onChange: (e) => {
        inputProps?.onChange?.({
          ...e,
          target: {
            ...e.target,
            value: formatValue(e?.target?.value, false, true)
          }
        })
      }
    };
  }, [richText, mentions, multiline, rows, minRows,
    maxRows, inputProps, formatValue, placeholder, size, EditorProps]);

  const renderInput = () => {
    if (innerProps.readOnly) {
      return <Box className="TextField-readOnly Input-readOnly">
        {InputProps?.startAdornment}
        {renderValue()}
        {InputProps?.endAdornment}
      </Box>
    } else {
      return <InputComponent id={id}
                             name={name}
                             value={internalValue}
                             label={label}
                             type={type}
                             size={size}
                             minRows={multiline ? (minRows ?? 2) : null}
                             maxRows={multiline ? (maxRows ?? 6) : null}
                             multiline={multiline}
                             placeholder={hiddenPlaceholder ? '' : placeholder ?? label}
                             className="TextField-input"
                             autoComplete="off"
                             onChange={handleChange}
                             inputProps={inputPropsMemo}
                             {...utils.cleanObject({
                               inputComponent: richText ? EditorInput : (
                                 mentions ? MentionsInput : null
                               )
                             })}
                             {...InputProps}/>
    }
  }

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledTextField ref={innerRef} {...innerProps}>
    {!innerProps.hiddenLabel ?
      <InputLabel size={size}
                  {...utils.cleanObject({
                    htmlFor: (!(innerProps.readOnly || innerProps.disabled) ? id : null),
                    component: (innerProps.readOnly || innerProps.disabled) ? 'span' : null,
                    shrink: innerProps.readOnly || innerProps.disabled || null
                  })}
                  className={utils.classNames('TextField-label', InputLabelProps?.className)}
                  {...InputLabelProps}>
        {label}
      </InputLabel> : null}
    <InputContainer className="TextField-container">
      {renderInput()}
      {!innerProps.hiddenHelperText ?
        <FormHelperText component="div" {...FormHelperTextProps}
                        className={utils.classNames('TextField-helper', FormHelperTextProps?.className)}>
          {helperText}
        </FormHelperText> : null}
    </InputContainer>
  </StyledTextField>
}));

TextField.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.any,
  placeholder: PropTypes.any,
  value: PropTypes.any,
  type: PropTypes.string,
  helperText: PropTypes.any,
  size: PropTypes.string,
  rows: PropTypes.number,
  minRows: PropTypes.number,
  maxRows: PropTypes.number,
  multiline: PropTypes.bool,
  markdown: PropTypes.bool,
  richText: PropTypes.bool,
  trim: PropTypes.bool,
  format: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  autoFocus: PropTypes.bool,
  ellipsis: PropTypes.bool,
  showTooltip: PropTypes.bool,
  readOnly: PropTypes.bool,
  renderReadOnly: PropTypes.func,
  inputProps: PropTypes.object,
  InputProps: PropTypes.object,
  InputLabelProps: PropTypes.object,
  EditorProps: PropTypes.object,
  MarkdownProps: PropTypes.object,
  FormHelperTextProps: PropTypes.object
};

export default TextField;
